"use client";

import { DirectusImage } from "@/components/DirectusImage";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";


export default function NewClient(props: {
  data: {
    tabName?: string;
    data: {
      title?: string;
      description?: string;
      image?: string;
    }[];
  }[];
}) {
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);

  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const [thumbsRef, thumbApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    loop: true,
    align: "center",
    breakpoints: {
      "(min-width: 768px)": {
        loop: true,
        dragFree: true,
        align: "center",
        containScroll: "keepSnaps",
      },
    },
  });

  const [mainCurrent, setMainCurrent] = useState(0);

  const [thumbCurrent, setThumbCurrent] = useState(0);

  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});

  const [isChanging, setIsChanging] = useState(false);

  const preloadImage = (imageId: string) => {
    if (!imageId) return;
    
    setLoadingImages(prev => ({ ...prev, [imageId]: true }));
    
    const img = new Image();
    img.src = `${process.env.NEXT_PUBLIC_ASSETS}/${imageId}`;
    img.onload = () => {
      setLoadingImages(prev => ({ ...prev, [imageId]: false }));
    };
  };

  useEffect(() => {
    const currentTabImages = props.data[thumbCurrent]?.data?.map(item => item.image) || [];
    currentTabImages.forEach(imageId => {
      if (imageId) preloadImage(imageId);
    });
  }, [thumbCurrent, props.data]);

  const onMainSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }
    setMainCurrent(mainApi.selectedScrollSnap() || 0);
    mainApi?.scrollTo(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  const onThumbSelect = useCallback(() => {
    if (!thumbApi) return;
    
    setIsChanging(true);
    const nextIndex = thumbApi.selectedScrollSnap() || 0;
    
    setTimeout(() => {
      setThumbCurrent(nextIndex);
      setIsChanging(false);
    }, 400);
    
    thumbApi?.scrollTo(nextIndex);
  }, [thumbApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    setScrollSnaps(mainApi.scrollSnapList());

    mainApi.on("reInit", () => {
      setScrollSnaps(mainApi.scrollSnapList());
    });
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onMainSelect();
    mainApi.on("select", onMainSelect).on("reInit", onMainSelect);
  }, [mainApi, onMainSelect]);

  useEffect(() => {
    if (!thumbApi) {
      return;
    }
    onThumbSelect();
    thumbApi.on("select", onThumbSelect).on("reInit", onThumbSelect);
  }, [thumbApi, onThumbSelect]);

  return (
    <div className="pt-8">
      <div className="content_container relative px-10 xl:px-16">
        <div className="mb-3 md:mb-10">
          <div className="relative overflow-hidden" ref={thumbsRef}>
            <div
              className={cn("-ml-10 flex md:-ml-[70px]", {
                "justify-center": props.data.length <= 2,
              })}>
              {props.data.map((el, index) => (
                <div
                  key={index}
                  className="min-w-0 shrink-0 basis-auto pl-10 md:basis-auto md:pl-[70px]">
                  <div
                    className={cn(
                      "relative flex w-full select-none cursor-pointer items-center justify-center pb-2 pt-2 text-sm text-[#383838] after:absolute after:bottom-0 after:left-1/2 after:h-0 after:w-0 after:-translate-x-1/2 after:transition-all after:duration-300 after:ease-in-out md:text-lg",
                      {
                        "text-black md:after:h-1 md:after:w-full md:after:bg-[#383838]":
                          thumbCurrent === index,
                      }
                    )}
                    onClick={() => {
                      thumbApi?.scrollTo(index);
                    }}>
                    {el.tabName}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="absolute left-3 top-0 z-10 flex h-9 w-6 cursor-pointer items-center justify-center border-none transition-colors duration-300 after:absolute after:-left-4 after:h-full after:w-14 after:bg-gradient-to-r after:from-white after:to-transparent hover:text-primary md:left-0 md:h-11 md:bg-white md:after:left-6 md:after:w-24"
              onClick={() => {
                thumbApi?.scrollPrev();
              }}>
              <ChevronLeft />
            </div>

            <div
              className="absolute right-3 top-0 flex h-9 w-6 cursor-pointer items-center justify-center border-none transition-colors duration-300 after:absolute after:-right-4 after:h-full after:w-14 after:bg-gradient-to-l after:from-white after:to-transparent hover:text-primary md:right-0 md:h-11 md:bg-white md:after:right-6 md:after:w-24"
              onClick={() => {
                thumbApi?.scrollNext();
              }}>
              <ChevronRight />
            </div>
          </div>
        </div>
      </div>

      <Carousel 
        className={cn(
          "relative transition-all duration-500",
          isChanging ? "opacity-80" : "opacity-100"
        )} 
        opts={{ 
          loop: true,
          skipSnaps: false,
          duration: 20,
        }} 
        setApi={setMainApi}>
        <CarouselContent>
          {props.data?.[thumbCurrent]?.data?.map((el, index) => (
            <CarouselItem
              key={index}
              className="basis-[80vw] pl-4 md:basis-[52.35vw] md:pl-14">
              <div
                className={cn(
                  "relative h-[156px] w-full select-none md:h-[560px]",
                  "transition-all duration-500",
                  {
                    "opacity-50": mainCurrent !== index,
                    "opacity-80": isChanging,
                  }
                )}>
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center overflow-hidden rounded-2xl">
                  {loadingImages[el.image || ''] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 backdrop-blur-sm">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  )}
                  
                  <DirectusImage
                    id={el.image || ""}
                    alt={el.title || ""}
                    width={1200}
                    height={560}
                    className={cn(
                      "h-full w-full object-cover transition-opacity duration-500",
                      loadingImages[el.image || ''] ? 'opacity-0' : 'opacity-100'
                    )}
                    onLoad={() => {
                      setLoadingImages(prev => ({ ...prev, [el.image || '']: false }));
                    }}
                  />
                </div>

                <div className="absolute bottom-0 left-0 top-0 h-full w-full rounded-2xl bg-gradient-to-l from-transparent to-[#383838] md:w-3/5"></div>
                <div className={cn(
                  "relative z-10 flex h-full w-full flex-col justify-center px-3 text-white md:justify-end md:px-8 lg:px-12",
                  "transition-transform duration-500",
                  loadingImages[el.image || ''] ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'
                )}>
                  <div className="md:h-1/2 lg:w-1/2">
                    <div className="text-lg font-bold md:text-xl lg:text-2xl">
                      {el.title}
                    </div>
                    <div className="mt-3 text-xs font-medium md:text-sm">
                      {el.description}
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious className="left-[11vw] hidden h-12 w-12 border-primary border-opacity-30 bg-white bg-opacity-50 text-primary md:flex" />
        <CarouselNext className="right-[11vw] hidden h-12 w-12 border-primary border-opacity-30 bg-white bg-opacity-50 text-primary md:flex" />
      </Carousel>

      <div className="mt-4 flex w-full items-center justify-center space-x-[10px] md:hidden">
        {scrollSnaps.map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-2 w-2 rounded-full bg-[#CCCCCC] transition-all duration-300 ease-in-out",
              {
                "w-5 bg-[#2C2C2C]": mainCurrent === index,
              }
            )}></div>
        ))}
      </div>
    </div>
  );
}
